import React from "react";
import { makeStyles, Link as MuiLink, Button, Typography, useMediaQuery, useTheme } from "@material-ui/core";
import { useRouter } from "next/router";

import Logo from "./assets/footer_logo.svg";
import Facebook from "./assets/facebook.svg";
import Instagram from "./assets/instagram.svg";
import Linkedin from "./assets/linkedin.svg";
import Twitter from "./assets/twitter.svg";
import SubscribeNewsletter from "../SubscribeNewsletter";

const list = [
  {
    title: "Nos services",
    pages: [
      { label: "Faire réparer mon appareil ", url: "/" },
      { label: "Acheter un appareil", url: "/reconditionne" },
      { label: "Faire un auto-diagnostic", url: "/product-selection" },
    ],
    contact: {
      url: "https://murfy.zendesk.com/hc/fr/articles/360018601859-Comment-contacter-Murfy-",
      label: "Nous contacter",
    },
  },
  {
    title: "À propos",
    pages: [
      { label: "Nos engagements", url: "/nos-engagements" },
      { label: "Pourquoi réparer ?", url: "/pourquoi-reparer-electromenager" },
      { label: "C'est quoi le reconditionné ?", url: "/le-reconditionne" },
      { label: "Le blog", url: "/blog/" },
    ],
    contact: { url: "/nous-rejoindre", label: "Nous rejoindre" },
  },
];

const socials = [
  {
    logo: Facebook,
    url: "https://www.facebook.com/Murfy.fr/",
  },
  {
    logo: Instagram,
    url: "https://www.instagram.com/murfy.fr/",
  },
  {
    logo: Linkedin,
    url: "https://www.linkedin.com/company/murfy-repair/",
  },
  {
    logo: Twitter,
    url: "https://twitter.com/MurfyFR",
  },
];

const reconditionneLegals = [
  {
    label: "Charte de protection des données",
    url: "/charte-de-protection-des-donnees",
  },
  {
    label: "Mentions légales / CGU",
    url: "/mentions-legales-cgu",
  },
  {
    label: "Conditions générales de vente",
    url: "/conditions-generales-de-vente-reco",
  },
];

const reparationLegals = [
  {
    label: "Charte de protection des données",
    url: "/charte-de-protection-des-donnees",
  },
  {
    label: "Mentions légales / CGU",
    url: "/mentions-legales-cgu",
  },
  {
    label: "Conditions générales de vente",
    url: "/conditions-generales-rendez-vous-reparateur",
  },
];

const Footer = () => {
  const classes = useStyle();
  const router = useRouter();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));

  return (
    <div className={classes.container}>
      <div className={classes.content}>
        <div className={classes.logo}>
          <a href="/">
            <Logo />
          </a>
        </div>

        <div className={classes.subcontent}>
          <div className={classes.lists}>
            {list.map((category) => {
              return (
                <div className={classes.list} key={category.title}>
                  <Typography variant="body2" className={classes.title}>
                    {category.title}
                  </Typography>
                  <ul>
                    {category.pages.map(({ url, label }) => {
                      return (
                        <MuiLink href={url} key={label} color="inherit">
                          <li className={classes.item}>{label}</li>
                        </MuiLink>
                      );
                    })}
                  </ul>
                  <MuiLink
                    href={category.contact.url}
                    key={category.contact.label}
                    color="primary"
                    className={classes.contactLink}
                  >
                    {category.contact.label}
                  </MuiLink>
                </div>
              );
            })}
          </div>
        </div>
        <div className={classes.contacts}>
          <Typography color="white" variant="small">
            Pour recevoir les astuces Murfy, inscrivez-vous à la newsletter
          </Typography>
          <SubscribeNewsletter />
          <Button
            variant="contained"
            color="primary"
            className={classes.button}
            href="https://murfy.zendesk.com/hc/fr/"
            target="_blank"
            rel="noopener"
          >
            Foire aux questions
          </Button>
        </div>
      </div>
      <div className={classes.socials}>
        {socials.map(({ logo, url }) => {
          return (
            <a href={url} target="_blank" rel="noopener noreferrer" key={url}>
              {logo()}
            </a>
          );
        })}
      </div>
      <div className={classes.legals__container}>
        <div className={classes.credits}>
          <Typography variant="caption">© {new Date().getFullYear()} - Murfy</Typography>
        </div>
        <div className={classes.legals}>
          {router.pathname.includes("reconditionne")
            ? reconditionneLegals
                .map(({ label, url }) => {
                  return (
                    <MuiLink key={label} href={url} color="inherit">
                      {label}
                    </MuiLink>
                  );
                })
                .reduce((prev, curr) => [prev, `${isMobile ? "" : "/"}`, curr])
            : reparationLegals
                .map(({ label, url }) => {
                  return (
                    <MuiLink key={label} href={url} color="inherit">
                      {label}
                    </MuiLink>
                  );
                })
                .reduce((prev, curr) => [prev, `${isMobile ? "" : "/"}`, curr])}
        </div>
      </div>
    </div>
  );
};

const useStyle = makeStyles((theme) => ({
  container: {
    backgroundColor: theme.palette.secondary.dark,
    width: "100%",
    color: "white",
    padding: "128px 10% 20px 10%",
    [theme.breakpoints.down("md")]: {
      padding: "64px 5% 20px 5%",
    },

    "& a": {
      textDecoration: "none",
    },

    "& ul": {
      padding: "0",
      margin: "0",
    },
  },
  legals: {
    display: "flex",
    alignItems: "flex-start",
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
    },
    color: "white",
  },
  contacts: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down("md")]: {
      alignItems: "flex-start",
      marginTop: theme.spacing(3),
    },
    "& a": {
      textDecoration: "none",
    },
  },
  content: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",

    [theme.breakpoints.down("md")]: {
      flexDirection: "column",
    },
  },
  subcontent: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    width: "60%",
    height: "max-content",

    [theme.breakpoints.down("md")]: {
      width: "100%",
    },
  },
  contactLink: {
    marginTop: theme.spacing(),
    fontWeight: 700,
    textAlign: "end",
  },
  logo: {
    padding: "0 10px",
    marginRight: 120,
    [theme.breakpoints.down("md")]: {
      marginBottom: theme.spacing(4),
    },
    "& svg": {
      transition: "400ms",
      "&:hover": {
        cursor: "pointer",
        transform: "rotate(360deg)",
        transition: "1000ms",
      },
    },
    [theme.breakpoints.down("xs")]: {
      paddingLeft: 0,
    },
  },
  lists: {
    display: "flex",
    justifyContent: "flex-start",
    "& > :first-child": {
      marginRight: 130,
    },
    [theme.breakpoints.down("xs")]: {
      "& > :first-child": {
        paddingLeft: 0,
        marginRight: 30,
      },
    },
  },
  list: {
    padding: "0 10px",
  },
  title: {
    fontWeight: 700,
    fontSize: "15px",
    lineHeight: "140%",
    margin: "0 0 24px 0",
  },
  item: {
    listStyleType: "none",
    color: "white",
    textDecoration: "none",
    marginBottom: "8px",
  },
  button: {
    whiteSpace: "nowrap",
    padding: "5px 15px",
    marginBottom: theme.spacing(2),
    "& span": {
      fontWeight: 500,
      color: theme.palette.secondary.main,
    },
  },
  socials: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: theme.spacing(2),
    "& a": {
      marginLeft: theme.spacing(3),
      marginRight: 0,
    },
    [theme.breakpoints.down("md")]: {
      justifyContent: "flex-start",
      "& a": {
        marginLeft: 0,
        marginRight: theme.spacing(3),
      },
    },
    [theme.breakpoints.down("xs")]: {
      justifyContent: "space-between",
      "& a": {
        marginRight: 0,
      },
    },
  },
  legals__container: {
    marginTop: "120px",
    display: "flex",
    padding: "0 0 0 10px",
    alignItems: "center",
    justifyContent: "space-between",

    [theme.breakpoints.down("md")]: {
      flexDirection: "column-reverse",
      marginTop: "50px",
      alignItems: "flex-start",
    },
    [theme.breakpoints.down("xs")]: {
      paddingLeft: 0,
    },
    "& span": {
      color: "white",
    },
    "& a": {
      marginLeft: 4,
      marginRight: 4,
      fontSize: "13px",
      [theme.breakpoints.down("md")]: {
        margin: 0,
      },
    },
  },
}));

export default React.memo(Footer);
